import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Input from '../components/Input';
import Select from '../components/Select';
import Button from '../components/Button';
import Card from '../components/Card';
import OrcidButton from '../components/OrcidButton';
import GoogleButton from '../components/GoogleButton';
import DomainMultiSelect from '../components/DomainMultiSelect';

// Validation helpers
const validateName = (name) => {
  if (!name.trim()) return 'Name is required';
  if (name.trim().length < 2) return 'Name must be at least 2 characters';
  if (name.trim().length > 100) return 'Name must not exceed 100 characters';
  if (!/^[a-zA-Z\s'-]+$/.test(name.trim())) return 'Name can only contain letters, spaces, hyphens, and apostrophes';
  return '';
};

const validateEmail = (email) => {
  if (!email.trim()) return 'Email is required';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Please provide a valid email address';
  return '';
};

const validatePassword = (password) => {
  if (!password) return 'Password is required';
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (password.length > 128) return 'Password must not exceed 128 characters';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
  if (!/[@$!%*?&]/.test(password)) return 'Password must contain at least one special character (@$!%*?&)';
  return '';
};

const validRoles = ['author', 'reviewer', 'participant'];

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, verifyOtp, user } = useAuth();

  const roleParam = searchParams.get('role');

  const isValidRole = roleParam && validRoles.includes(roleParam);

  const [showOtp, setShowOtp] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [otp, setOtp] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: roleParam || '',
    expertiseDomains: []
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Update formData when roleParam changes via client-side navigation
  useEffect(() => {
    if (roleParam && validRoles.includes(roleParam)) {
      setFormData(prev => ({ ...prev, role: roleParam }));
    }
  }, [roleParam]);

  useEffect(() => {
    if (roleParam === 'organizer' || roleParam === 'admin') {
      navigate('/login');
    } else if (user) {
      navigate(`/${user.role}/dashboard`);
    }
  }, [user, navigate, roleParam]);

  const roleOptions = [
    { value: 'author', label: 'Author' },
    { value: 'reviewer', label: 'Reviewer' },
    { value: 'participant', label: 'Participant' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setError('');

    // Clear field error on change
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};

    errors.name = validateName(formData.name);
    errors.email = validateEmail(formData.email);
    errors.password = validatePassword(formData.password);

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.role) {
      errors.role = 'Please select a role';
    }

    // Filter out empty strings
    const activeErrors = {};
    Object.keys(errors).forEach(key => {
      if (errors[key]) activeErrors[key] = errors[key];
    });

    setFieldErrors(activeErrors);
    return Object.keys(activeErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      setTimeout(() => {
        const firstErrorEl = document.querySelector('.border-red-500');
        if (firstErrorEl) {
          firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstErrorEl.focus?.();
        }
      }, 100);
      return;
    }

    setLoading(true);

    // Prepare data
    const userData = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      role: formData.role,
      expertiseDomains: formData.expertiseDomains || []
    };

    const result = await register(userData);

    if (result.success) {
      if (result.requiresOtp) {
        setRegisteredEmail(result.email);
        setShowOtp(true);
        setLoading(false);
      } else {
        navigate(`/${result.user.role}/dashboard`);
      }
    } else {
      // Check if backend returned validation errors array
      if (result.errors && Array.isArray(result.errors)) {
        const backendFieldErrors = {};
        result.errors.forEach(err => {
          const field = err.path || err.param;
          if (field) backendFieldErrors[field] = err.msg;
        });
        setFieldErrors(backendFieldErrors);
      }
      setError(result.message);
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');

    if (!otp.trim()) {
      setFieldErrors({ otp: 'OTP is required' });
      return;
    }
    if (!/^\d{6}$/.test(otp.trim())) {
      setFieldErrors({ otp: 'OTP must be exactly 6 digits' });
      return;
    }

    setLoading(true);

    const result = await verifyOtp(registeredEmail, otp);

    if (result.success) {
      navigate(`/${result.user.role}/dashboard`);
    } else {
      setError(result.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        {/* Logo/Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img
              src="/resized-logo.jpg"
              alt="eConfMate Logo"
              className="h-16 w-16 object-contain transition-transform duration-200 hover:scale-110"
            />
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-600 via-primary-700 to-blue-600 bg-clip-text text-transparent">
              eConfMate
            </h1>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Create Account
          </h2>
          <p className="text-gray-600">
            {isValidRole ? `Register as ${roleParam}` : 'Join our conference management platform'}
          </p>
        </div>

        {/* Register Card */}
        {!isValidRole ? (
          <Card>
            <div className="mb-2">
              <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">Select Your Role</h3>
              <p className="text-gray-500 text-center mb-8">Choose how you want to join the platform</p>
              
              <div className="space-y-4">
                {[
                  { 
                    id: 'author', 
                    name: 'Author', 
                    desc: 'Submit and track research papers', 
                    icon: (
                      <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9zm3.75 11.625a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                      </svg>
                    ),
                    bg: 'bg-emerald-50',
                    borderHover: 'hover:border-emerald-500',
                    ringHover: 'hover:ring-emerald-500'
                  },
                  { 
                    id: 'reviewer', 
                    name: 'Reviewer', 
                    desc: 'Evaluate submissions & provide feedback', 
                    icon: (
                      <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                      </svg>
                    ),
                    bg: 'bg-purple-50',
                    borderHover: 'hover:border-purple-500',
                    ringHover: 'hover:ring-purple-500'
                  },
                  { 
                    id: 'participant', 
                    name: 'Participant', 
                    desc: 'Register for events and attend sessions', 
                    icon: (
                      <svg className="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z" />
                      </svg>
                    ),
                    bg: 'bg-orange-50',
                    borderHover: 'hover:border-orange-500',
                    ringHover: 'hover:ring-orange-500'
                  }
                ].map(r => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => navigate(`/register?role=${r.id}`)}
                    className={`w-full flex items-center p-4 rounded-xl border border-gray-200 bg-white transition-all duration-200 group hover:shadow-md ${r.borderHover} hover:ring-1 ${r.ringHover}`}
                  >
                    <div className={`flex-shrink-0 w-12 h-12 rounded-lg ${r.bg} flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-200`}>
                      {r.icon}
                    </div>
                    <div className="text-left flex-grow">
                      <div className="text-base font-semibold text-gray-900">{r.name}</div>
                      <div className="text-sm text-gray-500 mt-0.5">{r.desc}</div>
                    </div>
                    <div className="flex-shrink-0 ml-4 text-gray-300 group-hover:text-blue-600 transition-colors">
                      <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </Card>
        ) : (
        <Card>
          {showOtp ? (
            <form onSubmit={handleVerifyOtp}>
              <div className="text-center mb-6">
                <div className="bg-green-100 text-green-700 p-4 rounded-lg mb-4">
                  Registration successful! We've sent a 6-digit OTP to <strong>{registeredEmail}</strong>.
                </div>
                <p className="text-gray-600">Please enter the OTP below to verify your email and complete your registration.</p>
              </div>
              
              <Input
                label="One-Time Password (OTP)"
                type="text"
                name="otp"
                value={otp}
                onChange={(e) => { setOtp(e.target.value); setFieldErrors(prev => ({ ...prev, otp: '' })); }}
                placeholder="Enter 6-digit OTP"
                required
                error={fieldErrors.otp}
              />

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                fullWidth
                disabled={loading}
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleSubmit}>
            <Input
              label="Full Name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="John Doe"
              required
              error={fieldErrors.name}
            />

            <Input
              label="Email Address"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              required
              error={fieldErrors.email}
            />

            <Select
              label="Role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              options={roleOptions}
              required
              disabled={!!roleParam}
            />

            {(formData.role === 'reviewer' || formData.role === 'author') && (
              <div className="mb-4">
                <DomainMultiSelect
                  label="Expertise Domains"
                  value={formData.expertiseDomains}
                  onChange={(domains) => setFormData(prev => ({ ...prev, expertiseDomains: domains }))}
                  placeholder="Select your expertise domains..."
                />
              </div>
            )}

            <Input
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Min 8 chars, upper, lower, number, special"
              required
              error={fieldErrors.password}
            />

            <Input
              label="Confirm Password"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Repeat password"
              required
              error={fieldErrors.confirmPassword}
            />

            {/* Password requirements hint */}
            <div className="mb-4 -mt-2 text-xs text-gray-500">
              <p className="font-medium mb-1">Password must contain:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li className={formData.password.length >= 8 ? 'text-green-600' : ''}>At least 8 characters</li>
                <li className={/[A-Z]/.test(formData.password) ? 'text-green-600' : ''}>One uppercase letter</li>
                <li className={/[a-z]/.test(formData.password) ? 'text-green-600' : ''}>One lowercase letter</li>
                <li className={/[0-9]/.test(formData.password) ? 'text-green-600' : ''}>One number</li>
                <li className={/[@$!%*?&]/.test(formData.password) ? 'text-green-600' : ''}>One special character (@$!%*?&)</li>
              </ul>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>
          )}

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or register with</span>
            </div>
          </div>

          {/* ORCID Registration Button */}
          <OrcidButton role={roleParam} />

          {/* Google Registration Button */}
          <div className="mt-3">
            <GoogleButton role={roleParam} />
          </div>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link
                to={roleParam ? `/login?role=${roleParam}` : '/login'}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </Card>
        )}

        <div className="mt-6 text-center">
          <Link
            to="/"
            className="text-gray-600 hover:text-gray-900 font-medium"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
